<template>
  <Dialog :commonDialog="documentDialog" :dialog-width="800">
    <template v-slot:title>
      {{ isEditMode ? "Edit Incident Report" : "Add Incident Report" }}
    </template>
    <template v-slot:body>
      <v-form ref="incidentForm" v-model="formValid">
        <v-container>
          <v-row>
            <!-- Type Radio Buttons -->
            <v-col cols="12">
              <label class="font-weight-600 font-size-18 required">Type</label>
              <RadioInputCustom
                v-model="formData.type"
                row
                :items="[
                  {
                    label: 'Positive',
                    color: 'green darken-3 white--text',
                    value: 'positive',
                  },
                  {
                    label: 'Moderate',
                    color: 'warning darken-3 white--text',
                    value: 'moderate',
                  },
                  {
                    label: 'Negative',
                    color: 'red darken-4 white--text',
                    value: 'negative',
                  },
                ]"
                :rules="[validateRules.required(formData.type, 'Type')]"
                :disabled="formLoading"
              />
            </v-col>

            <v-col cols="6">
              <label class="font-weight-600 font-size-18 required"
                >Customer</label
              >
              <customer-dropdown
                :disabled="formLoading || isEditMode"
                :loading="formLoading"
                v-model="formData.customer"
                :rules="[validateRules.required(formData.customer, 'Customer')]"
              />

            </v-col>
            <!-- Visit -->
            <v-col cols="6">
              <label class="font-weight-600 font-size-18 required">Visit</label>
              <v-autocomplete
                :disabled="formLoading || !!selectedVisitId || isEditMode"
                :loading="formLoading"
                :items="visitList"
                v-model="formData.visit_id"
                hide-details
                clearable
                flat
                solo
                dense
                item-value="id"
                item-text="label"
                placeholder="Select Visit"
                color="cyan"
                item-color="cyan"
                class="pt-0 mt-0 mr-4"
                :rules="[validateRules.required(formData.visit_id, 'Visit')]"
              />
            </v-col>
            <!-- Date Picker -->
            <v-col cols="6">
              <label class="font-weight-600 font-size-18 required">Date</label>
              <DatePicker
                v-model="formData.incident_date"
                :rules="[
                  validateRules.required(
                    formData.incident_date,
                    'Incident Date'
                  ),
                ]"
                :disabled="formLoading"
              ></DatePicker>
            </v-col>

            <!-- Amount Damage -->
            <v-col cols="6">
              <label class="font-weight-600 font-size-18">Amount Damage</label>
              <v-text-field
                v-model.number="formData.amount_damage"
                type="number"
                placeholder="Amount Damage"
                dense
                filled
                solo
                flat
                :disabled="formLoading"
              ></v-text-field>
            </v-col>

            <!-- Tags (read-only in edit mode) -->
            <v-col cols="6">
              <label class="font-weight-600 font-size-18">Tags</label>
              <tag-auto-complete
                :type="tag_type"
                v-model="formData.tag"
                placeholder="Tags"
                :class="{ 'readonly-grey': isEditMode }"
                :style="isEditMode ? 'pointer-events: none;' : ''"
                :tabindex="isEditMode ? -1 : 0"
              />
            </v-col>

            <!-- Remark -->
            <v-col cols="12">
              <label class="font-weight-600 font-size-18">Remark</label>
              <TextAreaField
                auto-grow
                dense
                filled
                color="cyan"
                v-model.trim="formData.remark"
                placeholder="Remarks"
                solo
                flat
                row-height="25"
                counter="150"
                :disabled="formLoading"
              />
            </v-col>

            <!-- Attachments (read-only in edit mode) -->
            <v-col cols="12" v-if="!isEditMode">
              <DocumnetList
                label="Attachments"
                v-model="formData.attachments"
                :document-type="3"
                tag-type="incident"
                :disabled="isEditMode"
              ></DocumnetList>
            </v-col>
            <!-- Rating -->
            <v-col cols="12">
              <label class="font-weight-600 font-size-18 required d-flex"
                >Rating
              </label>
              <v-rating
                v-model="formData.rating"
                background-color="orange lighten-3"
                color="orange"
                label="Rating"
                size="60"
                style="max-width: 100%"
                :length="ratingOptions.length"
                :disabled="formLoading"
              ></v-rating>

              <span class="justify-center d-flex">
                <h5>Rating: {{ formData.rating }}</h5>
              </span>
            </v-col>

            <!-- Show on Mobile (read-only in edit mode) -->
            <v-col cols="12">
              <v-checkbox
                v-model="formData.show_on_mobile"
                label="Show on Mobile App"
                :disabled="isEditMode"
              ></v-checkbox>
            </v-col>
          </v-row>
        </v-container>
      </v-form>
    </template>
    <template v-slot:action>
      <v-btn
        depressed
        color="cyan"
        class="white--text"
        :loading="formLoading"
        :disabled="!formValid || formLoading"
        @click="submitForm"
      >
        {{ isEditMode ? "Update" : "Save" }}
      </v-btn>
      <v-btn depressed class="mx-2" :disabled="formLoading" @click="cancel()">
        Cancel
      </v-btn>
    </template>
  </Dialog>
</template>

<script>
import Dialog from "@/view/pages/partials/Dialog";
import DocumnetList from "@/view/pages/DocumnetList";
import DatePicker from "@/view/components/QDatePicker";
import ValidationMixin from "@/core/plugins/validation-mixin";
import { POST, GET } from "@/core/services/store/request.module";
import RadioInputCustom from "@/view/components/RadioInputCustom.vue";
import TagAutoComplete from "@/view/components/TagAutoCompleteInput.vue";
import moment from "moment";
import CustomerDropdown from "@/view/components/Customer-Dropdown.vue";
export default {
  name: "InternalIncidentReport",
  mixins: [ValidationMixin],
  components: {
    Dialog,
    DocumnetList,
    DatePicker,
    RadioInputCustom,
    CustomerDropdown,
    "tag-auto-complete": TagAutoComplete,
  },
  props: {
    typeId: {
      type: Number,
      default: 0,
    },
    visitlistingtype: {
      type: String,
      default: "all",
    },
    selectedVisitId: {
      type: Number,
      default: 0,
    },
    documentDialog: {
      type: Boolean,
      default: false,
    },
    editData: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      formValid: false,
      formLoading: false,
      customerList: [],
      tag_type: "engineer-incidents-report",
      ratingOptions: {
        length: 10,
      },
      visitList: [],
      formData: {
        type: "positive",
        customer: null,
        incident_date: null,
        amount_damage: null,
        tag: [],
        attachments: [],
        remark: null,
        rating: null,
        show_on_mobile: false,
        visit_id: null,
      },
      isEditMode: false,
    };
  },
  watch: {
    documentDialog: {
      handler(param) {
        if (param) {
          this.getAllCustomers();
          this.getAllVisits();
          if (this.selectedVisitId) {
            this.formData.visit_id = this.selectedVisitId;
          }
          if (this.editData) {
            this.isEditMode = true;
            this.prefillEditData();
          } else {
            this.isEditMode = false;
          }
        }
      },
      immediate: true,
    },
    editData: {
      async handler(newVal) {
        if (newVal) {
          this.isEditMode = true;
          this.formData.type = newVal.type || "positive";
          await this.fetchRatingSetting();
          this.prefillEditData();
        } else {
          this.isEditMode = false;
        }
      },
      immediate: true,
    },
    selectedVisitId: {
      handler(newVal) {
        if (newVal) {
          this.formData.visit_id = newVal;
        }
      },
      immediate: true,
    },
    typeId: {
      handler(newVal) {
        console.log(newVal);
        if (newVal > 0) {
          this.typeId = newVal;
          this.getAllVisits();
        }
      },
      immediate: true,
    },
    "formData.type": {
      async handler() {
        await this.fetchRatingSetting();
        if (!this.isEditMode) {
          this.formData.rating = this.ratingOptions.length;
        }
      },
      immediate: true,
    },
  },
  methods: {
    prefillEditData() {
      console.log(this.editData);

      if (!this.editData) return;
      this.formData.type = this.editData.type || "positive";
      this.formData.customer = this.editData.customer || null;
      this.formData.incident_date = this.editData.incident_date || null;
      this.formData.amount_damage = this.editData.amount_damage || null;
      this.formData.tag = this.editData.tags || [];
      this.formData.attachments = this.editData.attachments || [];
      this.formData.remark = this.editData.remark || null;
      this.formData.rating = Number(this.editData.rating);
      this.formData.show_on_mobile = this.editData.show_on_mobile || false;
      this.formData.visit_id = this.editData.visit_id || null;
    },
    resetForm() {
      this.formData = {
        type: "positive",
        customer: null,
        incident_date: null,
        amount_damage: null,
        tag: [],
        attachments: [],
        remark: null,
        rating: this.ratingOptions.length,
        show_on_mobile: false,
        visit_id: this.selectedVisitId || null,
      };
      this.$refs.incidentForm.resetValidation();
    },
    submitForm() {
      if (!this.$refs.incidentForm.validate()) return;
      this.formLoading = true;
      if (this.isEditMode && this.editData && this.editData.id) {
        this.$store
          .dispatch(POST, {
            url: `technician/incident/update/${this.typeId}/${this.editData.id}`,
            data: {
              type: this.formData.type,
              visit_id: this.formData.visit_id,
              customer: this.formData.customer.id,
              incident_date: moment(this.formData.incident_date).format(
                "YYYY-MM-DD"
              ),
              amount_damage: this.formData.amount_damage,
              remark: this.formData.remark,
              rating: this.formData.rating,
              show_on_mobile: this.formData.show_on_mobile,

              attachments: this.formData.attachments,
            },
          })
          .then(() => {
            this.$emit("success", true);
            this.resetForm();
            this.$emit("close");
          })
          .catch((error) => {
            this.logError(error);
          })
          .finally(() => {
            this.formLoading = false;
          });
      } else {
        this.$store
          .dispatch(POST, {
            url: `technician/incident/${this.typeId}`,
            data: this.formData,
          })
          .then(() => {
            this.$emit("success", true);
            this.resetForm();
            this.$emit("close");
          })
          .catch((error) => {
            this.logError(error);
          })
          .finally(() => {
            this.formLoading = false;
          });
      }
    },
    getAllCustomers() {
      this.formLoading = true;
      this.$store
        .dispatch(GET, { url: "customer-list" })
        .then(({ data }) => {
          this.customerList = data;
        })
        .catch((error) => {
          this.logError(error);
        })
        .finally(() => {
          this.formLoading = false;
        });
    },
    cancel() {
      this.resetForm();
      this.$emit("close");
    },
    async fetchRatingSetting() {
      this.formLoading = true;
      try {
        const { data } = await this.$store.dispatch(GET, {
          url: `setting/rating`,
        });

        if (this.formData.type === "positive") {
          this.ratingOptions.length = data.positive_rating;
        } else if (this.formData.type === "moderate") {
          this.ratingOptions.length = data.moderate_rating;
        } else if (this.formData.type === "negative") {
          this.ratingOptions.length = data.negative_rating;
        }
        // this.formData.rating = this.ratingOptions.length;
      } catch (error) {
        this.logError(error);
      } finally {
        this.formLoading = false;
      }
    },
    async getAllVisits() {
      this.formLoading = true;
      try {
        const { data } = await this.$store.dispatch(GET, {
          url: `ticket/engineers-visits/${this.typeId}`,
        });
        this.visitList = (data.rows || []).map((v) => ({
          id: Number(v.id),
          barcode: v.barcode,
          visit_title: v.visit_title,
          label: `${v.barcode} - ${v.visit_title}`,
        }));

        const ensureVisitId =
          this.selectedVisitId || (this.editData && this.editData.visit_id);

        if (ensureVisitId) {
          let found = this.visitList.find((v) => v.id === ensureVisitId);
          if (!found) {
            const selected = (data.rows || []).find(
              (v) => v.id === ensureVisitId
            );
            if (selected) {
              found = {
                id: selected.id,
                barcode: selected.barcode,
                visit_title: selected.visit_title,
                label: `${selected.barcode} - ${selected.visit_title}`,
              };
              this.visitList.unshift(found);
            }
          }
          this.$nextTick(() => {
            this.formData.visit_id = Number(ensureVisitId);
          });
        }
      } catch (error) {
        this.logError(error);
      } finally {
        this.formLoading = false;
      }
    },
  },

  mounted() {
    this.formData.rating = this.ratingOptions.length;

    this.getAllCustomers();
    if (this.typeId > 0 || this.lodash.isEmpty(this.editData) == false) {
      this.getAllVisits();
    }
  },
};
</script>

<style>
.readonly-grey .v-input__slot,
.readonly-grey input,
.readonly-grey .v-select__slot {
  background: rgba(0, 36, 138, 0.0784313725) !important;
  color: #e4dddd !important;
  font-weight: 100;
  cursor: not-allowed !important;
}
</style>
